import { useContext, useEffect, useState } from 'react';
import { ROLES } from '../../constants/GlobalConstants';
import { AppContext } from '../../contexts/AppContext';
import { LoginContext } from '../../contexts/LoginContext';
import ClaimsStatsAPI from '../../services/ClaimsStatsAPI';
import { YMD_FORMAT } from '../../utils/DateUtils';

const chartColors = ['#5778a4', '#e49444', '#d1615d', '#6a9f58', '#fff530', '#a87c9f', '#967662', '#85b6b2', '#86fc58', '#b8b0ac'];

const useClaimStatsPresenter = () => {
  const { showError, showWarning } = useContext(AppContext);
  const { userInfo } = useContext(LoginContext);

  const [showChildren, setShowChildren] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [animateChart, setAnimateChart] = useState(true);

  const [filterByDate, setFilterByDate] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const cleanFilters = () => {
    setDateFrom(null);
    setDateTo(null);
  };

  const handleSelection = (group, groupType) => {
    search({ [groupType]: group.description }).then(res =>
      setSelectedGroups([
        ...selectedGroups,
        {
          description: group.description,
          total: res.total,
          chartData: res.reasonResult,
          groupChildren: res.groupResult,
          groupType: groupType,
          childrenGroupType: res.groupType,
        },
      ])
    );
  };

  const removeLastGroup = () => {
    setShowChildren(false);
    setSelectedGroups(selectedGroups.slice(0, -1));
  };

  const search = (breakdown = {}) => {
    setLoading(true);
    setShowChildren(false);

    const request = {
      fromDate: filterByDate ? dateFrom.format(YMD_FORMAT) : undefined,
      toDate: filterByDate ? dateTo.format(YMD_FORMAT) : undefined,
      ...breakdown,
    };

    return ClaimsStatsAPI.getStats(request)
      .then(res => {
        return res;
      })
      .catch(error => showError('No se pudo obtener estadísticas de reclamos'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    search().then(res =>
      setSelectedGroups([
        {
          description: 'Total de reclamos en la empresa',
          total: res.total,
          chartData: res.reasonResult,
          groupChildren: res.groupResult,
          childrenGroupType: res.groupType,
        },
      ])
    );
  }, []);

  const validateFilters = () => {
    let errors = [];
    const errorMessages = [];
    let i = 0;

    if ((errors[i++] = filterByDate && (!dateFrom || !dateTo))) {
      errorMessages.push('Debe completar el rango de fechas');
    }

    const hasErrors = errors.some(e => e);

    if (hasErrors) {
      setAnimateChart(false);
      showWarning(errorMessages);
    } else {
      setAnimateChart(true);
    }

    return !hasErrors;
  };

  const validateAndSearch = () => {
    if (validateFilters()) {
      search().then(res =>
        setSelectedGroups([
          {
            description: 'Total de reclamos en la empresa',
            total: res.total,
            chartData: res.reasonResult,
            groupChildren: res.groupResult,
            childrenGroupType: res.groupType,
          },
        ])
      );
    }
  };

  return {
    filterProps: {
      filterByDate,
      setFilterByDate,
      dateFrom,
      setDateFrom,
      dateTo,
      setDateTo,
      cleanFilters,
      validateAndSearch,
    },
    selectedGroupsProps: {
      selectedGroups,
      setShowChildren,
      hasChildren: !!selectedGroups.last()?.groupChildren && !selectedGroups.last()?.groupChildren.isEmpty(),
      removeLastGroup,
      loading,
    },
    childSelectionProps: {
      setShowChildren,
      group: selectedGroups.last(),
      handleSelection,
    },
    detailsProps: {
      chartData: selectedGroups.last()?.chartData,
      loading,
    },
    chartProps: {
      chartColors,
      chartData: selectedGroups.last()?.chartData,
      loading,
      animateChart,
    },
    showChildren,
    showBreakdown: [ROLES.ADMIN, ROLES.AUDIT].includes(userInfo.role),
  };
};

export default useClaimStatsPresenter;
